import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Mail, CheckCircle2, AlertCircle, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '../lib/api';

export const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, setAuth } = useAuth();
  const token = searchParams.get('token');

  const [verifying, setVerifying] = useState(!!token);
  const [verified, setVerified] = useState(false);
  const [verifyError, setVerifyError] = useState(null);

  const [resending, setResending] = useState(false);
  const [resendEmail, setResendEmail] = useState('');

  // If user is logged in and already verified, offer direct escape
  const alreadyVerified = isAuthenticated && user?.email_verified_at;

  // Auto-verify token on mount
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const doVerify = async () => {
      try {
        const res = await authAPI.verifyEmail(token);
        if (cancelled) return;
        setVerified(true);
        setVerifyError(null);
        toast.success(res.data.message || 'Email verified successfully');

        // Update auth context with fresh tokens so subsequent API calls
        // have email_verified=true in the JWT (prevents 403 loop)
        if (res.data.access_token && res.data.refresh_token && res.data.user) {
          await setAuth({
            token: res.data.access_token,
            refreshToken: res.data.refresh_token,
            user: res.data.user,
          });
        }
      } catch (err) {
        if (cancelled) return;
        const detail = err.response?.data?.detail || 'Verification failed';
        // "already used" is still a success scenario
        if (err.response?.data?.already_verified || detail.toLowerCase().includes('already')) {
          setVerified(true);
          setVerifyError(null);
        } else {
          setVerifyError(detail);
        }
      } finally {
        if (!cancelled) setVerifying(false);
      }
    };

    doVerify();
    return () => { cancelled = true; };
  }, [token]);

  // Resend verification
  const handleResend = async () => {
    const email = isAuthenticated ? user?.email : resendEmail.trim();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setResending(true);
    try {
      const res = await authAPI.resendVerification(email);
      if (res.data?.already_verified) {
        toast.success('Your email is already verified. Please log in.');
        setVerified(true);
      } else {
        toast.success('Verification email sent. Check your inbox.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send verification email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // ignore
    }
    navigate('/login');
  };

  // ---- Verified state (token worked or already verified) ----
  if (verified || alreadyVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md" data-testid="verify-email-card">
          <CardHeader className="text-center pb-2">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <CardTitle className="text-xl">Email Verified</CardTitle>
            <CardDescription>
              {alreadyVerified
                ? "Your email is already verified."
                : "Your email has been verified successfully."}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {isAuthenticated ? (
              <Button
                className="w-full"
                onClick={() => navigate('/dashboard')}
                data-testid="go-to-dashboard-button"
              >
                Continue to Dashboard
              </Button>
            ) : (
              <Button
                className="w-full"
                onClick={() => navigate('/login?verified=1')}
                data-testid="go-to-login-button"
              >
                Go to Login
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Verifying in progress ----
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md" data-testid="verify-email-card">
          <CardContent className="flex flex-col items-center py-12">
            <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Verifying your email...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Error from token verification ----
  if (verifyError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md" data-testid="verify-email-card">
          <CardHeader className="text-center pb-2">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-3" />
            <CardTitle className="text-xl">Verification Failed</CardTitle>
            <CardDescription>{verifyError}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <p className="text-sm text-center text-muted-foreground">
              Request a new verification email below.
            </p>
            {!isAuthenticated && (
              <div className="space-y-1.5">
                <Label className="text-sm">Email address</Label>
                <Input
                  type="email"
                  placeholder="you@company.com"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  data-testid="resend-email-input"
                />
              </div>
            )}
            <Button
              onClick={handleResend}
              disabled={resending}
              className="w-full gap-2"
              data-testid="resend-button"
            >
              {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              {resending ? 'Sending...' : 'Resend Verification Email'}
            </Button>
            <Button variant="outline" onClick={() => navigate('/login')} className="w-full">
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Default: waiting for verification (no token in URL) ----
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md" data-testid="verify-email-card">
        <CardHeader className="text-center pb-2">
          <Mail className="w-12 h-12 text-primary mx-auto mb-3" />
          <CardTitle className="text-xl">Verify Your Email</CardTitle>
          <CardDescription>
            {isAuthenticated && user?.email
              ? `We sent a verification link to ${user.email}.`
              : 'Check your inbox for the verification link.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground text-center">
            Click the link in your email to verify your account.
            If you don't see it, check your spam folder.
          </p>

          {/* Resend section */}
          {!isAuthenticated && (
            <div className="space-y-1.5">
              <Label className="text-sm">Email address</Label>
              <Input
                type="email"
                placeholder="you@company.com"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                data-testid="resend-email-input"
              />
            </div>
          )}

          <Button
            onClick={handleResend}
            disabled={resending}
            variant="outline"
            className="w-full gap-2"
            data-testid="resend-button"
          >
            {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            {resending ? 'Sending...' : 'Resend Verification Email'}
          </Button>

          {/* Safety escape: already verified or stuck */}
          <div className="border-t pt-4 mt-2 space-y-3">
            <p className="text-sm text-muted-foreground text-center" data-testid="already-verified-hint">
              Already verified? Log out and back in to refresh your session.
            </p>
            {isAuthenticated ? (
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="w-full gap-2 text-muted-foreground"
                data-testid="verify-page-logout-button"
              >
                <LogOut className="w-4 h-4" />
                Log Out
              </Button>
            ) : (
              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                className="w-full text-muted-foreground"
              >
                Go to Login
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
